import { useCallback, useEffect, useMemo, useState } from "react";
import imageCompression from "browser-image-compression";
import { FileUploadType } from "@fixa/platform-shared/dist/fileUpload/fileUpload.type";
import { useWriteSignedUrls } from "@coworker/apprestructured/src/task-management/services/useWriteSignedUrls";
import { useUploadFilesToBucket } from "@coworker/apprestructured/src/task-management/services/useUploadFilesToBucket";
import {
  calcHasSignedUrlsForAllFiles,
  convertToSignedFiles,
  newOrExistingFilesToNewOrExistingUploads,
  newOrExistingUploadsToCreateUploads,
  UploaderError,
  UploaderResult,
} from "@coworker/apprestructured/src/uploader/uploader.helper";
import {
  NewOrExistingFile,
  newOrExistingFilesToFiles,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { NewOrExistingUpload } from "@coworker/apprestructured/src/task-management/types/Upload";

const NO_NEW_OR_EXISTING_UPLOADS: NewOrExistingUpload[] = [];

type ImageUploaderOptions = {
  maxWidthOrHeight?: number;
};

/**
 * Helper for uploading images to a bucket.
 * Images are compressed, resized and converted to image/jpeg before uploading.
 */
export function useImageUploader(
  newOrExistingFiles: NewOrExistingFile[],
  options: ImageUploaderOptions = DEFAULT_OPTIONS
): UploaderResult {
  const [compressedImages, setCompressedImages] = useState<NewOrExistingFile[]>(
    []
  );

  const newFiles = useMemo(() => {
    return newOrExistingFilesToFiles(newOrExistingFiles);
  }, [newOrExistingFiles]);

  const newCompressedFiles = useMemo(() => {
    return newOrExistingFilesToFiles(compressedImages);
  }, [compressedImages]);

  // Needs to be an effect because it is async
  useEffect(() => {
    async function fn() {
      const arr: NewOrExistingFile[] = [];
      for (let i = 0; i < newOrExistingFiles.length; i++) {
        const newOrExistingFile = newOrExistingFiles[i]!;
        switch (newOrExistingFile.type) {
          case "new": {
            arr.push({
              ...newOrExistingFile,
              file: await compressImage(newOrExistingFile.file, options),
            });
            break;
          }
          case "existing": {
            arr.push(newOrExistingFile);
            break;
          }
        }
      }
      setCompressedImages(arr);
    }

    fn();
  }, [newOrExistingFiles, options]);

  const allNewFilesAreCompressed = useMemo(() => {
    return calcAllFilesAreCompressed(newFiles, newCompressedFiles);
  }, [newCompressedFiles, newFiles]);

  const compressedImageFileNames = useMemo(() => {
    return newCompressedFiles.map((file) => file.name);
  }, [newCompressedFiles]);

  const { data: signedUrlResponses, error: writeSignedUrlsError } =
    useWriteSignedUrls(compressedImageFileNames, FileUploadType.IMAGE);

  const hasSignedUrlsForAllNewCompressedFiles = useMemo(() => {
    return calcHasSignedUrlsForAllFiles(newCompressedFiles, signedUrlResponses);
  }, [newCompressedFiles, signedUrlResponses]);

  const { mutateAsync: uploadFilesToBucketMutation } = useUploadFilesToBucket();

  const ready =
    allNewFilesAreCompressed && hasSignedUrlsForAllNewCompressedFiles;

  const upload = useCallback(() => {
    if (!ready) {
      throw new Error("Image uploader is not ready");
    }
    const signedFiles = convertToSignedFiles(
      newCompressedFiles,
      signedUrlResponses
    );
    return uploadFilesToBucketMutation(signedFiles);
  }, [
    ready,
    newCompressedFiles,
    signedUrlResponses,
    uploadFilesToBucketMutation,
  ]);

  const newOrExistingUploads = useMemo(() => {
    if (!hasSignedUrlsForAllNewCompressedFiles) {
      return NO_NEW_OR_EXISTING_UPLOADS;
    }
    return newOrExistingFilesToNewOrExistingUploads(
      compressedImages,
      signedUrlResponses
    );
  }, [
    compressedImages,
    hasSignedUrlsForAllNewCompressedFiles,
    signedUrlResponses,
  ]);

  const createUploads = useMemo(() => {
    return newOrExistingUploadsToCreateUploads(newOrExistingUploads);
  }, [newOrExistingUploads]);

  const error = useMemo<UploaderError | null>(() => {
    return writeSignedUrlsError ? "FAILED_TO_FETCH_WRITE_SIGNED_URLS" : null;
  }, [writeSignedUrlsError]);

  return { ready, upload, newOrExistingUploads, createUploads, error };
}

const DEFAULT_MAX_WIDTH_OR_HEIGHT = 1000;

const DEFAULT_OPTIONS: ImageUploaderOptions = {
  maxWidthOrHeight: DEFAULT_MAX_WIDTH_OR_HEIGHT,
};

async function compressImage(image: File, options: ImageUploaderOptions) {
  const compressedImage = await imageCompression(image, {
    ...options,
    fileType: "image/jpeg",
  });

  return new File(
    [compressedImage],
    image.name.replace(/\.[^/.]+$/, "") + ".jpg",
    { type: compressedImage.type, lastModified: Date.now() }
  );
}

function calcAllFilesAreCompressed(files: File[], compressedFiles: File[]) {
  if (files.length !== compressedFiles.length) {
    return false;
  }

  const fileNamesWithoutExtension = calcFileNamesWithoutExtension(files);
  const compressedFileNamesWithoutExtension =
    calcFileNamesWithoutExtension(compressedFiles);
  for (let i = 0; i < files.length; i++) {
    if (
      fileNamesWithoutExtension[i] !== compressedFileNamesWithoutExtension[i]
    ) {
      return false;
    }
  }
  return true;
}

function calcFileNamesWithoutExtension(files: File[]) {
  return files.map(calcFileNameWithoutExtension);
}

function calcFileNameWithoutExtension(file: File) {
  if (!file.name.includes(".")) {
    return file.name;
  }
  return file.name.substring(0, file.name.lastIndexOf("."));
}
